import { useCallback } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Autocomplete, ControlLabelCheckBox, FormField, Typography, useCommonComponentContext } from "@maysoft/common-component-react";

import Strings from "constants/strings";

import { BooleanInt, Mode } from "constants/enum";
import { ICodename, IDataDetailReport, IErrorReport } from "commons/interfaces";



const OTHER_TITLE = "3";

const makeReporterAnonymous = (data: IDataDetailReport) => {
    data.anonymousReporter = BooleanInt.True;
    data.reporter = "";
    data.reporterPhone = "";
    data.reporterEmail = "";
    data.detector = "";
    data.detectorTitle = "";
    data.titleNote = "";
    data.witnessName1 = "";
    data.witnessName2 = "";
    return data;
}

const CardReporterInfo = ({
    mode,
    data,
    error,
    dataOptions,
    handleChangeValue,
    handleChangeMultiValue,
    resetReporter,
}: {
    mode: Mode,
    error: IErrorReport;
    data: IDataDetailReport;
    handleChangeValue: (key: string, value: any) => void;
    handleChangeMultiValue?: (data: IDataDetailReport) => void;
    resetReporter?: () => void;
    dataOptions: {
        staffTitle: ICodename[];
    };
}) => {
    const { userInfo } = useCommonComponentContext();

    const isAnonymous = data?.anonymousReporter === BooleanInt.True;
    const anonymousDisabled: boolean = isAnonymous && data?.createUser !== userInfo?.userProfile?.identityId;

    const handleCheckAnonymous = useCallback((checked: boolean) => {
        if (checked) {
            handleChangeMultiValue(makeReporterAnonymous(data));
        } else {
            handleChangeValue("anonymousReporter", BooleanInt.False);
            resetReporter?.();
        }
    }, [resetReporter, handleChangeMultiValue]);

    return (
        <Card sx={{ padding: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h6">
                        {Strings.REPORT.REPORTER_INFO}
                    </Typography>
                </Grid>

                {/* Báo cáo tự nguyện */}
                {data?.selectedType === "0" && (
                    <Grid item xs={12}>
                        <ControlLabelCheckBox
                            disabled={mode === Mode.View || anonymousDisabled}
                            label={Strings.REPORT.ANONYMOUS_REPORT}
                            value={data?.anonymousReporter === BooleanInt.True}
                            onChangeValue={handleCheckAnonymous}
                        />
                    </Grid>
                )}
                <Grid item xs={12} sm={6}>
                    <FormField
                        disabled
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.REPORT.FULL_NAME}
                        defaultValue={data?.reporter || ""}
                        errorMessage={error?.reporter || ""}
                        placeholder={Strings.REPORT.ENTER_FULL_NAME}
                        onBlur={(val) => {
                            handleChangeValue("reporter", val);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        disabled={isAnonymous}
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.REPORT.PHONE_NUMBER}
                        defaultValue={data?.reporterPhone || ""}
                        errorMessage={error?.reporterPhone || ""}
                        placeholder={Strings.REPORT.ENTER_PHONE_NUMBER}
                        onBlur={(val) => {
                            handleChangeValue("reporterPhone", val);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        disabled={isAnonymous}
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.REPORT.EMAIL}
                        placeholder={Strings.REPORT.ENTER_EMAIL}
                        defaultValue={data?.reporterEmail || ""}
                        errorMessage={error?.reporterEmail || ""}
                        onBlur={(val) => {
                            handleChangeValue("reporterEmail", val);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        disabled={isAnonymous}
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.REPORT.DETECTOR}
                        defaultValue={data?.detector || ""}
                        errorMessage={error?.detector || ""}
                        placeholder={Strings.REPORT.ENTER_DETECTOR}
                        onBlur={(val) => {
                            handleChangeValue("detector", val);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        disabled={isAnonymous}
                        mode={mode}
                        variant={"outlined"}
                        data={dataOptions.staffTitle}
                        defaultValue={data?.detectorTitle}
                        errorMessage={error?.detectorTitle}
                        label={Strings.REPORT.DETECTOR_TITLE}
                        placeholder={Strings.REPORT.SELECT_DETECTOR_TITLE}
                        onChange={(val) => {
                            handleChangeValue("detectorTitle", val);
                        }}
                    />
                </Grid>
                {
                    (data?.detectorTitle === OTHER_TITLE) &&
                    <Grid item xs={12} sm={6}>
                        <FormField
                            disabled={isAnonymous}
                            mode={mode}
                            variant={"outlined"}
                            defaultValue={data?.titleNote || ""}
                            errorMessage={error?.titleNote || ""}
                            placeholder={Strings.REPORT.ENTER_DETECTOR_TITLE}
                            onBlur={(val) => {
                                handleChangeValue("titleNote", val);
                            }}
                        />
                    </Grid>
                }
                <Grid item xs={12} sm={6}>
                    <FormField
                        disabled={isAnonymous}
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.REPORT.WITNESSER_1}
                        defaultValue={data?.witnessName1 || ""}
                        errorMessage={error?.witnessName1 || ""}
                        placeholder={Strings.REPORT.ENTER_WITNESSES_1}
                        onBlur={(val) => {
                            handleChangeValue("witnessName1", val);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        disabled={isAnonymous}
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.REPORT.WITNESSER_2}
                        defaultValue={data?.witnessName2 || ""}
                        errorMessage={error?.witnessName2 || ""}
                        placeholder={Strings.REPORT.ENTER_WITNESSES_2}
                        onBlur={(val) => {
                            handleChangeValue("witnessName2", val);
                        }}
                    />
                </Grid>
            </Grid>
        </Card>
    );
}

export default CardReporterInfo;